import React from 'react';

import { Alert, Stack, Typography } from '@mui/material';

import type {
  EmailDeliveryStatus,
  EmailDeliveryStatusKey,
} from '~/hooks/apis/customer-communication/useEmailDeliveryStatus';
import { useUpdateEmailDeliveryStatus } from '~/hooks/apis/customer-communication/useEmailDeliveryStatus';

import InstructionActionPanel from './InstructionActionPanel';
import InstructionInfoPanel from './InstructionInfoPanel';
import StatusColumn from './StatusColumn';
import { InstructionBody, STATUS_CONFIG, SUPPRESSION_TYPES } from './constants';

interface Props {
  isLoading: boolean;
  isRefetching: boolean;
  isFetchError: boolean;
  deliveryStatus: EmailDeliveryStatus | undefined;
  selectedKey: EmailDeliveryStatusKey | null;
  instructionBody: InstructionBody | null;
  checkedSteps: Array<boolean>;
  mutation: ReturnType<typeof useUpdateEmailDeliveryStatus>;
  onSelect: (key: EmailDeliveryStatusKey) => void;
  onDismiss: () => void;
  onToggleStep: (index: number) => void;
  onRemove: () => void;
}

export default function StatusDialogBody({
  isLoading,
  isRefetching,
  isFetchError,
  deliveryStatus,
  selectedKey,
  instructionBody,
  checkedSteps,
  mutation,
  onSelect,
  onDismiss,
  onToggleStep,
  onRemove,
}: Props) {
  if (isLoading) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading…
      </Typography>
    );
  }

  if (isFetchError) {
    return (
      <Alert severity="error" sx={{ borderRadius: 1 }}>
        Unable to load email delivery status. This may be due to a permissions issue or a temporary service outage.
        Please try again later or contact engineering support if the problem persists.
      </Alert>
    );
  }

  if (!deliveryStatus) return null;

  return (
    <Stack spacing={2} sx={{ pt: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        Red or unknown statuses can be opened to view guidance or take action.
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        sx={{
          opacity: isRefetching ? 0.5 : 1,
          pointerEvents: isRefetching ? 'none' : 'auto',
          transition: 'opacity 0.2s ease',
        }}
      >
        {SUPPRESSION_TYPES.map((key) => {
          const config = STATUS_CONFIG[key];
          const status = deliveryStatus[key].status;
          return (
            <StatusColumn
              key={key}
              category={config.category}
              status={status}
              activeLabel={config.activeLabel}
              clearLabel={config.clearLabel}
              selected={selectedKey === key}
              onClick={() => onSelect(key)}
            />
          );
        })}
      </Stack>

      {instructionBody?.infoOnly && <InstructionInfoPanel body={instructionBody} onDismiss={onDismiss} />}

      {instructionBody && !instructionBody.infoOnly && (
        <InstructionActionPanel
          body={instructionBody}
          checkedSteps={checkedSteps}
          onToggleStep={onToggleStep}
          onRemove={onRemove}
          onDismiss={onDismiss}
          mutation={mutation}
        />
      )}
    </Stack>
  );
}
